/**
 * Client-side theme preference (P15-008, PWA & UX overhaul):
 * Supports the full Patches theme catalog: 'system', 'patches', 'dark', 'light',
 * 'paper', 'mono', 'hacker', 'pastel'.
 *
 * Persisted in `localStorage` and mirrored onto `<html data-theme>` and `<meta name="theme-color">`
 * so `index.css` selects the right CSS custom properties and the browser header matches seamlessly.
 * Never sent to the server — appearance is a cosmetic preference, and cosmetics never gate function (spec §184.3).
 */
export type ThemePreference =
  'system' | 'patches' | 'dark' | 'light' | 'paper' | 'mono' | 'hacker' | 'pastel';

export interface ThemeInfo {
  readonly id: ThemePreference;
  readonly name: string;
  readonly description: string;
  readonly preview: {
    readonly bg: string;
    readonly fg: string;
    readonly accent: string;
    readonly border: string;
  };
}

export const THEME_CATALOG: readonly ThemeInfo[] = [
  {
    id: 'system',
    name: 'Follow system',
    description: 'Matches your OS dark/light mode preference automatically.',
    preview: {
      bg: '#181825',
      fg: '#cdd6f4',
      accent: '#cba6f7',
      border: '#45475a',
    },
  },
  {
    id: 'patches',
    name: 'Patches',
    description: 'Dracula: soft violet on charcoal, pink and green accents.',
    preview: {
      bg: '#282a36',
      fg: '#f8f8f2',
      accent: '#bd93f9',
      border: '#4d5066',
    },
  },
  {
    id: 'dark',
    name: 'Dark',
    description: 'Clean modern slate dark with purple highlights.',
    preview: {
      bg: '#1e1e2e',
      fg: '#cdd6f4',
      accent: '#cba6f7',
      border: '#45475a',
    },
  },
  {
    id: 'light',
    name: 'Light',
    description: 'Clean crisp daylight theme with deep royal purple.',
    preview: {
      bg: '#eff1f5',
      fg: '#4c4f69',
      accent: '#8839ef',
      border: '#bcc0cc',
    },
  },
  {
    id: 'paper',
    name: 'Paper',
    description: 'Warm literary sepia parchment with rich espresso ink.',
    preview: {
      bg: '#f7f2e8',
      fg: '#26231f',
      accent: '#6a3dad',
      border: '#d7cae9',
    },
  },
  {
    id: 'mono',
    name: 'Mono',
    description: 'High-contrast stark terminal monochrome.',
    preview: {
      bg: '#000000',
      fg: '#ffffff',
      accent: '#ffffff',
      border: '#4a4a4a',
    },
  },
  {
    id: 'hacker',
    name: 'Hacker',
    description: 'Retro CRT phosphor green matrix terminal.',
    preview: {
      bg: '#001400',
      fg: '#d7ffd7',
      accent: '#00ff66',
      border: '#00cc55',
    },
  },
  {
    id: 'pastel',
    name: 'Pastel',
    description: 'Soft cyberpunk dreamy lavender and mint.',
    preview: {
      bg: '#24273a',
      fg: '#cad3f5',
      accent: '#f5bde6',
      border: '#494d64',
    },
  },
];

const STORAGE_KEY = 'patches.web.theme.v1';
const VALID_PREFERENCES: readonly ThemePreference[] = [
  'system',
  'patches',
  'dark',
  'light',
  'paper',
  'mono',
  'hacker',
  'pastel',
];

const THEME_META_COLORS: Readonly<Record<ThemePreference, string>> = {
  system: '#282a36',
  patches: '#282a36',
  dark: '#1e1e2e',
  light: '#eff1f5',
  paper: '#f7f2e8',
  mono: '#000000',
  hacker: '#001400',
  pastel: '#24273a',
};

type Listener = () => void;

function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (VALID_PREFERENCES as readonly string[]).includes(value);
}

function readFromStorage(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function applyToDocument(preference: ThemePreference): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', preference);

  // Sync meta theme-color for browser bar and PWA header
  let color = THEME_META_COLORS[preference];
  if (preference === 'system') {
    const dark = getSystemPrefersDark();
    color = dark ? THEME_META_COLORS.dark : THEME_META_COLORS.light;
  }
  const meta = document.getElementById('meta-theme-color');
  if (meta) {
    meta.setAttribute('content', color);
  }
}

let current: ThemePreference = readFromStorage();
const listeners = new Set<Listener>();

// Idempotent with inline pre-paint script
applyToDocument(current);

function notify(): void {
  for (const listener of listeners) listener();
}

export function getThemePreference(): ThemePreference {
  return current;
}

export function setThemePreference(preference: ThemePreference): void {
  current = preference;
  applyToDocument(preference);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Best-effort persistence
    }
  }
  notify();
}

export function subscribeThemePreference(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function subscribeSystemColorScheme(listener: Listener): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (): void => {
    if (current === 'system') {
      applyToDocument('system');
    }
    listener();
  };
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}

/** True when the OS/user asked the site to minimize motion (`prefers-reduced-motion: reduce`,
 * or the boolean media query form). Consumed by the identity-cosmetic-packs layer (B-117) so a
 * "pop" or pulsing frame never animates for a user who asked for less motion. */
export function getSystemPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Live subscription to the reduced-motion preference — pairs with
 * `getSystemPrefersReducedMotion` for a `useSyncExternalStore` binding. */
export function subscribeSystemPrefersReducedMotion(listener: Listener): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const handler = (): void => listener();
  media.addEventListener('change', handler);
  return () => media.removeEventListener('change', handler);
}
